var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import invariant from 'tiny-invariant';
import { _TypedDataEncoder } from '@ethersproject/hash';
import { MaxSigDeadline, MaxAllowanceTransferAmount, MaxUnorderedNonce, MaxSignatureTransferAmount, } from '../config/constants';
import { permit2Domain } from './domain';
export var PERMIT_DETAILS = [
    { name: 'token', type: 'address' },
    { name: 'amount', type: 'uint160' },
    { name: 'expiration', type: 'uint48' },
    { name: 'nonce', type: 'uint48' },
];
export var PERMIT_BATCH_TYPES = {
    PermitBatch: [
        { name: 'details', type: 'PermitDetails[]' },
        { name: 'spender', type: 'address' },
        { name: 'sigDeadline', type: 'uint256' },
    ],
    PermitDetails: PERMIT_DETAILS,
};
export var TOKEN_PERMISSIONS = [
    { name: 'token', type: 'address' },
    { name: 'amount', type: 'uint256' },
];
export var PERMIT_BATCH_TRANSFER_FROM_TYPES = {
    PermitBatchTransferFrom: [
        { name: 'permitted', type: 'TokenPermissions[]' },
        { name: 'nonce', type: 'uint256' },
        { name: 'deadline', type: 'uint256' },
    ],
    TokenPermissions: TOKEN_PERMISSIONS,
};
export var PERMIT_SINGLE_TYPES = {
    PermitSingle: [
        { name: 'details', type: 'PermitDetails' },
        { name: 'spender', type: 'address' },
        { name: 'sigDeadline', type: 'uint256' },
    ],
    PermitDetails: PERMIT_DETAILS,
};
export function validatePermitDetails(details) {
    invariant(MaxAllowanceTransferAmount.gte(details.amount), 'AMOUNT_OUT_OF_RANGE');
}
export var PERMIT_TYPES = {
    PermitSingle: [
        { name: 'details', type: 'PermitDetails' },
        { name: 'spender', type: 'address' },
        { name: 'sigDeadline', type: 'uint256' },
    ],
    PermitDetails: PERMIT_DETAILS,
};
function isPermit(permit) {
    return !Array.isArray(permit.details);
}
var AllowanceTransfer = /** @class */ (function () {
    /**
     * Cannot be constructed.
     */
    function AllowanceTransfer() {
    }
    // return the data to be sent in a eth_signTypedData RPC call
    // for signing the given permit data
    AllowanceTransfer.getPermitData = function (permit, permit2Address, chainId) {
        invariant(MaxSigDeadline.gte(permit.sigDeadline), 'SIG_DEADLINE_OUT_OF_RANGE');
        var domain = permit2Domain(permit2Address, chainId);
        if (isPermit(permit)) {
            validatePermitDetails(permit.details);
            return {
                domain: domain,
                types: PERMIT_TYPES,
                values: permit,
            };
        }
        else {
            permit.details.forEach(validatePermitDetails);
            return {
                domain: domain,
                types: PERMIT_BATCH_TYPES,
                values: permit,
            };
        }
    };
    AllowanceTransfer.hash = function (permit, permit2Address, chainId) {
        var _a = AllowanceTransfer.getPermitData(permit, permit2Address, chainId), domain = _a.domain, types = _a.types, values = _a.values;
        return _TypedDataEncoder.hash(domain, types, values);
    };
    return AllowanceTransfer;
}());
export { AllowanceTransfer };
var PERMIT_TRANSFER_FROM_TYPES = {
    PermitTransferFrom: [
        { name: 'permitted', type: 'TokenPermissions' },
        { name: 'spender', type: 'address' },
        { name: 'nonce', type: 'uint256' },
        { name: 'deadline', type: 'uint256' },
    ],
    TokenPermissions: TOKEN_PERMISSIONS,
};
function permitTransferFromWithWitnessType(witness) {
    return __assign({ PermitWitnessTransferFrom: [
            { name: 'permitted', type: 'TokenPermissions' },
            { name: 'spender', type: 'address' },
            { name: 'nonce', type: 'uint256' },
            { name: 'deadline', type: 'uint256' },
            { name: 'witness', type: witness.witnessTypeName },
        ], TokenPermissions: TOKEN_PERMISSIONS }, witness.witnessType);
}
function permitBatchTransferFromWithWitnessType(witness) {
    return __assign({ PermitBatchWitnessTransferFrom: [
            { name: 'permitted', type: 'TokenPermissions[]' },
            { name: 'spender', type: 'address' },
            { name: 'nonce', type: 'uint256' },
            { name: 'deadline', type: 'uint256' },
            { name: 'witness', type: witness.witnessTypeName },
        ], TokenPermissions: TOKEN_PERMISSIONS }, witness.witnessType);
}
function isPermitTransferFrom(permit) {
    return !Array.isArray(permit.permitted);
}
var SignatureTransfer = /** @class */ (function () {
    /**
     * Cannot be constructed.
     */
    function SignatureTransfer() {
    }
    // return the data to be sent in a eth_signTypedData RPC call
    // for signing the given permit data
    SignatureTransfer.getPermitData = function (permit, permit2Address, chainId, witness) {
        invariant(MaxSigDeadline.gte(permit.deadline), 'SIG_DEADLINE_OUT_OF_RANGE');
        invariant(MaxUnorderedNonce.gte(permit.nonce), 'NONCE_OUT_OF_RANGE');
        var domain = permit2Domain(permit2Address, chainId);
        if (isPermitTransferFrom(permit)) {
            validateTokenPermissions(permit.permitted);
            var types = witness
                ? permitTransferFromWithWitnessType(witness)
                : PERMIT_TRANSFER_FROM_TYPES;
            var values = witness ? Object.assign(permit, { witness: witness.witness }) : permit;
            return {
                domain: domain,
                types: types,
                values: values,
            };
        }
        else {
            permit.permitted.forEach(validateTokenPermissions);
            var types = witness
                ? permitBatchTransferFromWithWitnessType(witness)
                : PERMIT_BATCH_TRANSFER_FROM_TYPES;
            var values = witness ? Object.assign(permit, { witness: witness.witness }) : permit;
            return {
                domain: domain,
                types: types,
                values: values,
            };
        }
    };
    SignatureTransfer.hash = function (permit, permit2Address, chainId, witness) {
        var _a = SignatureTransfer.getPermitData(permit, permit2Address, chainId, witness), domain = _a.domain, types = _a.types, values = _a.values;
        return _TypedDataEncoder.hash(domain, types, values);
    };
    return SignatureTransfer;
}());
export { SignatureTransfer };
function validateTokenPermissions(permissions) {
    invariant(MaxSignatureTransferAmount.gte(permissions.amount), 'AMOUNT_OUT_OF_RANGE');
}
